import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Eye, EyeOff, Trash2 } from 'lucide-react';
import { Button, Input, Card, Alert, Loader } from '@dreamtree-org/twreact-ui';
import { useAppApi, useStableApi } from '@/lib/api';
import Can from '@rbac/Can';
import { visibleFields, staleFieldNames } from '@/lib/fieldVisibility';
import ModelCatalogScope from '@/components/ModelCatalogScope';
import { ConnectionStatusBadge } from '@pages/ConnectionsPage';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * Connection detail/edit/create page (`/app-connection/:id`, or `/new`) — the form is
 * rendered dynamically from the provider registry's field specs (GET
 * /api/connections/providers), so a new provider never needs a client change here.
 * Secret fields are write-only by default (VoiceSettingsPage's KeyField / AgentDetail's
 * ApiKeyControl pattern): values are never prefilled on load — only a has_credentials
 * boolean comes back — and credentials are sent only if the user typed into at least
 * one secret field (a whole-blob replacement server-side, so all required secret
 * fields must be filled together). The eye icon opts out on demand: it fetches the
 * decrypted set (rotate permission, rate-limited server-side) and prefills the form,
 * so single-field edits then save a complete blob.
 */
function apiError(ex) {
  return (ex.response && ex.response.data && ex.response.data.error) || ex.message;
}

function textInputType(fieldType) {
  if (fieldType === 'password') return 'password';
  if (fieldType === 'number') return 'number';
  return 'text';
}

function revealButtonLabel(busy, revealed) {
  if (busy) return 'Revealing…';
  return revealed ? 'Hide values' : 'Reveal values';
}

function FieldInput({ field, value, onChange, placeholderOverride, forceText }) {
  if (field.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm pt-5">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {field.label}
      </label>
    );
  }
  if (field.type === 'select') {
    return (
      <div>
        <label className="block text-sm mb-1">{field.label}</label>
        <select
          className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="" disabled>Select…</option>
          {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div className="col-span-2">
        <label className="block text-sm mb-1">{field.label}</label>
        <textarea
          className="w-full min-h-[120px] rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-sm font-mono"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholderOverride || field.placeholder || ''}
          autoComplete="off"
        />
      </div>
    );
  }
  // An empty password field renders as plain text: there is no secret to unmask, and
  // the browser-style eye toggle twreact-ui adds to password inputs was being mistaken
  // for the section's Reveal control. It flips back to password (masked, with the
  // unmask-what-you-typed eye) as soon as the field holds a value.
  const asText = field.type === 'password' && (forceText || !value);
  return (
    <Input
      label={field.label}
      type={asText ? 'text' : textInputType(field.type)}
      autoComplete="off"
      value={value ?? ''}
      placeholder={placeholderOverride || field.placeholder || ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Name/description + the category → provider cascade (both locked after create —
 *  their field specs differ per provider, so a swap would strand the stored blob). */
function MetaSection({ row, isNew, categories, categoryProviders, setField, onProviderContextChange, apps }) {
  return (
    <Card className="p-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Name" value={row.name || ''} onChange={(e) => setField('name', e.target.value)} />
        <Input label="Description" value={row.description || ''} onChange={(e) => setField('description', e.target.value)} />
        <div>
          <label className="block text-sm mb-1">Category</label>
          <select
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
            value={row.category_key || ''}
            disabled={!isNew}
            onChange={(e) => { setField('category_key', e.target.value); setField('provider', ''); onProviderContextChange(); }}
          >
            <option value="" disabled>Select a category…</option>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">Provider</label>
          <select
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
            value={row.provider || ''}
            disabled={!isNew || !row.category_key}
            onChange={(e) => { setField('provider', e.target.value); onProviderContextChange(); }}
          >
            <option value="" disabled>Select a provider…</option>
            {categoryProviders.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
      </div>
      {apps.length > 0 && (
        <div>
          <label className="block text-sm mb-1">Application</label>
          <select
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
            value={row.application_id != null ? row.application_id : ''}
            onChange={(e) => setField('application_id', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Company-wide (all apps)</option>
            {apps.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      )}
      {!isNew && <p className="text-xs text-secondary-500">Category and provider are locked after creation — create a new connection to switch.</p>}
    </Card>
  );
}

function ConfigSection({ providerSpec, config, setConfigField, setConfigValues, scope, isNew }) {
  if (!providerSpec || providerSpec.configFields.length === 0) return null;
  const fields = visibleFields(providerSpec.configFields, scope);
  return (
    <Card className="p-3 space-y-3">
      <div className="text-xs uppercase text-secondary-500">Configuration</div>
      {providerSpec.fileUpload === 'sqlite' && (
        <SqliteUploadSection config={config} setConfigValues={setConfigValues} isNew={isNew} />
      )}
      <div className="grid grid-cols-2 gap-3">
        {fields.map((field) => (
          <FieldInput key={field.name} field={field} value={config[field.name]} onChange={(v) => setConfigField(field.name, v)} />
        ))}
      </div>
    </Card>
  );
}

/** sqlite-dataset's upload control — POST /api/connections/sqlite-upload stages the file
 *  and returns 202 {uploadJobId}; parsing (Spaces upload + introspection) happens off the
 *  request, so this polls the status endpoint until it reports ready and only then prefills
 *  storage_path/filename/schema_json (resetting the table allowlist to "all"). Polls stop
 *  on unmount so navigating away can't leak a timer or set state on a dead component. */
const SQLITE_PARSE_POLL_MS = 1500;
const SQLITE_PARSE_TIMEOUT_MS = 5 * 60 * 1000;

function SqliteUploadSection({ config, setConfigValues, isNew }) {
  const { fetchApi } = useStableApi();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const inputRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const waitForParse = useCallback(async (uploadJobId) => {
    const startedAt = Date.now();
    for (;;) {
      if (!aliveRef.current) throw new Error('Upload cancelled');
      if (Date.now() - startedAt > SQLITE_PARSE_TIMEOUT_MS) {
        throw new Error('Parsing the SQLite file took too long — try again');
      }
      await new Promise((r) => setTimeout(r, SQLITE_PARSE_POLL_MS));
      const res = await fetchApi('connectionSqliteUploadStatus', uploadJobId);
      if (res.status === 'ready') return res;
      if (res.status === 'error') throw new Error(res.error || 'Could not parse the SQLite file');
    }
  }, []);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setErr('');
    setInfo('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetchApi('connectionSqliteUpload', formData);
      setInfo(`${file.name} — parsing tables…`);
      const parsed = await waitForParse(res.uploadJobId);
      setConfigValues({
        ...config,
        storage_path: parsed.storage_path,
        filename: parsed.filename,
        schema_json: parsed.schema_json || '',
        tables_json: '',
      });
      setInfo(`${parsed.filename} — ${parsed.table_count} table(s) found.`);
    } catch (ex) {
      setErr(apiError(ex));
      setInfo('');
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && config.storage_path) return null; // locked after create (provider swap would strand the object key)
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file" accept=".sqlite,.sqlite3,.db" className="hidden" onChange={onFileChange} />
        <Button size="sm" variant="secondary" onClick={() => inputRef.current?.click()} disabled={busy}>
          {busy ? 'Uploading…' : 'Upload SQLite file'}
        </Button>
        <span className="text-xs text-secondary-500">.sqlite / .sqlite3 / .db — stored privately</span>
        {info && <span className="text-xs text-emerald-700">{info}</span>}
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>
    </div>
  );
}

/** True when some field's showIf points at a gating field the user has not set yet — the
 *  difference between "not asked yet" and "asked, and this scheme needs nothing". */
function gatingFieldsUnanswered(fields, scope) {
  return (fields || []).some((f) => f.showIf && (scope || {})[f.showIf.field] === undefined);
}

/**
 * Secret fields are write-only by default; the eye icon reveals them on demand via
 * GET /api/connections/:id/credentials (server-side decrypt behind connections.rotate,
 * rate-limited). Revealing also prefills the form with the full current set, so
 * editing a single field after reveal saves a complete, correct blob — without the
 * reveal, the whole-set-replacement rule still applies.
 */
function CredentialsSection({ providerSpec, credentials, setCredential, hasCredentials, connectionId, isNew, onRevealed, scope }) {
  const { fetchApi } = useAppApi();
  const [revealed, setRevealed] = useState(false);
  const [revealBusy, setRevealBusy] = useState(false);
  const [revealErr, setRevealErr] = useState('');

  if (!providerSpec) return null;

  const canReveal = !isNew && hasCredentials;
  // Only the fields the chosen auth type actually uses — the server drops the rest on
  // write, so rendering them would collect secrets it is about to discard.
  const fields = visibleFields(providerSpec.credentialFields, scope);
  // Nothing to show can mean two different things: the gating field (mcp's auth_type) has
  // not been answered yet, or it has been answered with a scheme that stores no secret.
  const gatePending = !fields.length && gatingFieldsUnanswered(providerSpec.credentialFields, scope);

  const toggleReveal = async () => {
    setRevealErr('');
    if (revealed) { setRevealed(false); return; }
    setRevealBusy(true);
    try {
      const res = await fetchApi('connectionCredentials', connectionId);
      onRevealed(res.credentials || {});
      setRevealed(true);
    } catch (ex) {
      setRevealErr(apiError(ex));
    } finally {
      setRevealBusy(false);
    }
  };

  return (
    <Can module="connections" action="rotate" fallback={
      <Card className="p-3"><p className="text-sm text-secondary-500">Credentials {hasCredentials ? 'are configured' : 'are not set yet'} — editing them requires the connections.rotate permission.</p></Card>
    }>
      <Card className="p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase text-secondary-500">Credentials (encrypted at rest)</div>
          {canReveal && (
            <Button size="sm" variant="secondary" onClick={toggleReveal} disabled={revealBusy} aria-label={revealed ? 'Hide credentials' : 'Reveal credentials'}>
              {revealed ? <EyeOff size={14} className="mr-1 inline" /> : <Eye size={14} className="mr-1 inline" />}
              {revealButtonLabel(revealBusy, revealed)}
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {fields.map((field) => (
            <FieldInput
              key={field.name}
              field={field}
              value={credentials[field.name]}
              onChange={(v) => setCredential(field.name, v)}
              placeholderOverride={hasCredentials && !revealed ? 'Configured — enter a new value to replace' : field.placeholder}
              forceText={revealed}
            />
          ))}
        </div>
        {!fields.length && (
          <p className="text-sm text-secondary-500">
            {gatePending
              ? 'Pick the auth type above — it decides which credentials are asked for here.'
              : 'This connection needs no stored secret — nothing to fill in.'}
          </p>
        )}
        {revealErr && <p className="text-xs text-red-600">{revealErr}</p>}
        {hasCredentials && !revealed && !!fields.length && (
          <p className="text-xs text-secondary-500">
            Credentials are set. Leave every field blank to keep them; filling any field replaces the whole set — or click Reveal to view and edit the current values.
          </p>
        )}
        {revealed && (
          <p className="text-xs text-secondary-500">
            Showing the stored values. Edit any field and Save to update the set; Hide re-masks them.
          </p>
        )}
      </Card>
    </Can>
  );
}

/** Resolve when the OAuth popup reports back (source 'connection-oauth'), or when it closes
 *  without reporting — the caller re-reads the connection either way, so a blocked
 *  postMessage degrades to "refresh and see" rather than hanging. */
function waitForConnectionOAuth(popup) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(timer);
      resolve();
    };
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data && event.data.source === 'connection-oauth') finish();
    };
    window.addEventListener('message', onMessage);
    const timer = setInterval(() => { if (popup.closed) finish(); }, 500);
  });
}

/** Interactive OAuth for a connection configured with auth_type 'oauth_authorization_code':
 *  opens the authorization server in a popup, and on return re-reads the connection so the
 *  newly-stored tokens show up as "credentials configured". Shown only for the OAuth scheme;
 *  every other scheme uses the static credential form above. */
function OAuthConnectSection({ connectionId, isNew, isOAuthAuthType, onConnected }) {
  const { fetchApi } = useAppApi();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (!isOAuthAuthType || isNew) return null;

  const connect = async () => {
    setBusy(true);
    setErr('');
    try {
      const { authorizeUrl } = await fetchApi('connectionOauthStart', connectionId);
      const popup = window.open(authorizeUrl, 'connection-oauth', 'width=620,height=760');
      if (!popup) { setErr('Popup blocked — allow popups and try again.'); return; }
      await waitForConnectionOAuth(popup);
      onConnected();
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setErr('');
    try {
      await fetchApi('connectionOauthDisconnect', connectionId);
      onConnected();
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-3 space-y-2">
      <div className="text-xs uppercase text-secondary-500">OAuth authorization</div>
      <p className="text-sm text-secondary-500">
        Click Connect to open the provider in a new window, approve access, and capture the
        grant back here — no token pasting.
      </p>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="primary" onClick={connect} disabled={busy}>{busy ? 'Connecting…' : 'Connect'}</Button>
        <Button size="sm" variant="secondary" onClick={disconnect} disabled={busy}>Disconnect</Button>
      </div>
      {err && <p className="text-xs text-red-600">{err}</p>}
    </Card>
  );
}

function TestSection({ connectionId, hasCredentials, needsCredentials, status, lastTestError, onTested }) {
  const { fetchApi } = useAppApi();
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const runTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const res = await fetchApi('connectionTest', connectionId);
      setResult(res);
      onTested(res);
    } catch (ex) {
      setResult({ ok: false, error: apiError(ex) });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="p-3 space-y-2">
      <div className="flex items-center gap-3">
        <Can module="connections" action="rotate" fallback={<p className="text-sm text-secondary-500">Testing requires the connections.rotate permission.</p>}>
          <Button size="sm" variant="secondary" onClick={runTest} disabled={testing || (needsCredentials && !hasCredentials)}>
            {testing ? 'Testing…' : 'Test connection'}
          </Button>
        </Can>
        <ConnectionStatusBadge status={status} />
      </div>
      {needsCredentials && !hasCredentials && <p className="text-xs text-secondary-500">Save credentials first — the test calls the real provider with them.</p>}
      {result && !result.ok && <Alert variant="error" title="Test failed">{result.error}</Alert>}
      {result && result.ok && (
        <p className="text-sm text-emerald-600">Connected ({result.latencyMs}ms){result.detail ? ` — ${result.detail}` : ''}.</p>
      )}
      {!result && lastTestError && <p className="text-xs text-red-600">Last test error: {lastTestError}</p>}
    </Card>
  );
}

/**
 * The model catalogue for this connection, in context — the same card the AI Models
 * screen renders, scoped to one credential. Kept here as well as on that screen because
 * "what can this key actually run" is a question you ask while looking at the key.
 */
function ModelCatalogSection({ connectionId }) {
  const { fetchApi } = useAppApi();
  const [scope, setScope] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => fetchApi('modelCatalogList', { connection_id: connectionId })
    .then((res) => setScope((res?.scopes || [])[0] || null))
    .catch((ex) => setError(apiError(ex))), [connectionId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <Alert variant="error" title="Could not load models">{error}</Alert>;
  if (!scope) return null;
  return (
    <div className="space-y-1">
      <ModelCatalogScope scope={scope} onChanged={load} />
      <p className="text-xs text-secondary-500">
        Hidden models stay out of the agent screen&apos;s dropdown. Every credential&apos;s catalogue is on{' '}
        <Link className="text-primary-600" to="/app-provider-model">AI Models</Link>.
      </p>
    </div>
  );
}

/**
 * The tool providers this connection authenticates (app_tool_providers.auth_connection_id).
 * Bound from the provider's side on /app-tool-provider/:id; surfaced here so a credential
 * shows what it is actually wired to — and because an MCP connection with no server_url is
 * tested against the base_url of exactly these rows.
 */
function UsedBySection({ providers }) {
  if (!providers.length) return null;
  return (
    <Card className="p-3 space-y-2">
      <div className="text-xs uppercase text-secondary-500">Used by</div>
      <ul className="space-y-1">
        {providers.map((p) => (
          <li key={p.id} className="text-sm">
            <Link to={`/app-tool-provider/${p.id}`} className="text-primary-600 hover:underline">{p.name}</Link>
            <span className="text-secondary-500"> — {p.kind} tool provider{p.base_url ? ` · ${p.base_url}` : ''}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function HeaderSection({ row, isNew, saving, setField, save, remove }) {
  const saveDisabled = saving || !row.name || !row.category_key || !row.provider;
  return (
    <PageHeader
      backTo="/app-connection"
      backLabel="Connections"
      title={isNew ? 'New Connection' : row.name}
      actions={(
        <Can module="connections" action="author">
          {!isNew && (
            <>
              <label className="flex items-center gap-1 text-sm">
                <input type="checkbox" checked={!!row.is_active} onChange={(e) => setField('is_active', e.target.checked)} /> Active
              </label>
              <Button size="sm" variant="secondary" onClick={remove}><Trash2 size={14} className="mr-1 inline" /> Delete</Button>
            </>
          )}
          <Button size="sm" onClick={save} disabled={saveDisabled}>{saving ? 'Saving…' : 'Save'}</Button>
        </Can>
      )}
    />
  );
}

/** Everything the screen needs in one round of parallel requests. `list` responses come
 *  back either bare or wrapped in `.data` depending on the caller, hence `rows()`. */
function rows(res) {
  return (Array.isArray(res) ? res : res?.data) || [];
}

async function loadConnectionScreen(fetchApi, id, isNew) {
  const loads = [
    fetchApi('connectionProviders'),
    fetchApi('crud', { model: 'AppConnectionCategory', action: 'list', where: { is_active: true }, orderBy: { column: 'sort', direction: 'asc' }, limit: 100 }),
    fetchApi('applicationsList').catch(() => []),
  ];
  if (!isNew) {
    loads.push(fetchApi('crud', { model: 'AppConnection', action: 'show', where: { id } }));
    loads.push(fetchApi('crud', { model: 'AppToolProvider', action: 'list', where: { auth_connection_id: id }, limit: 50 }).catch(() => []));
  }
  const [specRes, catRes, appsRes, connRes, boundRes] = await Promise.all(loads);
  return {
    specRes,
    cats: rows(catRes),
    appList: rows(appsRes),
    conn: connRes ? (connRes.data || connRes) : null,
    bound: rows(boundRes),
  };
}

/** Drop keys from an object without mutating it. */
function withoutKeys(obj, keys) {
  return Object.fromEntries(Object.entries(obj).filter(([k]) => !keys.includes(k)));
}

async function removeConnection({ fetchApi, id, name, nav, setErr }) {
  if (!window.confirm(`Delete connection "${name}"? This cannot be undone.`)) return;
  try {
    await fetchApi('connectionDelete', id);
    nav('/app-connection');
  } catch (ex) {
    setErr(apiError(ex));
  }
}

const BLANK_CONNECTION = {
  name: '', description: '', category_key: '', provider: '', is_active: true,
  has_credentials: false, status: 'untested', application_id: null,
};

export default function ConnectionDetail() {
  const { id } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const { fetchApi } = useAppApi();

  const [spec, setSpec] = useState(null); // provider field specs (code registry)
  const [categories, setCategories] = useState([]); // DB rows — user-editable, incl. custom categories
  const [row, setRow] = useState(isNew ? { ...BLANK_CONNECTION } : null);
  const [config, setConfig] = useState({});
  const [credentials, setCredentials] = useState({});
  // Set when the user reveals the stored credentials — the form is prefilled with the
  // real values, so "touched" alone no longer means "changed". Save compares against
  // this snapshot to avoid re-encrypting an identical set (which would needlessly
  // reset the connection's tested status).
  const [revealedSnapshot, setRevealedSnapshot] = useState(null);
  const [apps, setApps] = useState([]);
  const [usedBy, setUsedBy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    loadConnectionScreen(fetchApi, id, isNew)
      .then(({ specRes, cats, appList, conn, bound }) => {
        setSpec(specRes);
        setCategories(cats);
        setApps(appList);
        setUsedBy(bound);
        if (conn) {
          setRow(conn);
          try { setConfig(JSON.parse(conn.config_json || '{}')); } catch { setConfig({}); }
        }
      })
      .catch((ex) => setErr(apiError(ex)))
      .finally(() => setLoading(false));
  }, [id]);

  const setField = (key, value) => setRow((r) => ({ ...r, [key]: value }));
  const setCredential = (key, value) => setCredentials((c) => ({ ...c, [key]: value }));
  const resetProviderState = () => { setConfig({}); setCredentials({}); setRevealedSnapshot(null); };
  const onRevealed = (values) => { setCredentials(values); setRevealedSnapshot(values); };

  const providerSpec = spec && row && row.provider ? spec.providers[row.provider] : null;
  // showIf resolves against config ∪ credentials, so one scope drives both grids.
  const scope = useMemo(() => ({ ...config, ...credentials }), [config, credentials]);

  // Changing a gating field (mcp's auth_type) must drop what the previous branch
  // collected — otherwise a bearer token typed first would still be sitting in state
  // when the user switches to Basic and saves.
  const setConfigField = (key, value) => {
    const nextConfig = { ...config, [key]: value };
    setConfig(nextConfig);
    const stale = providerSpec ? staleFieldNames(providerSpec.credentialFields, { ...nextConfig, ...credentials }) : [];
    if (stale.length) setCredentials((c) => withoutKeys(c, stale));
  };

  const categoryKey = row ? row.category_key : '';
  // Relational: each provider row carries its category, so a category's provider list
  // is derived — no stored membership anywhere.
  const categoryProviders = useMemo(() => {
    if (!spec || !categoryKey) return [];
    return Object.values(spec.providers).filter((p) => p.category === categoryKey && p.is_active);
  }, [spec, categoryKey]);

  // Whether the live test is blocked on a missing secret: a provider whose applicable
  // credential fields are all optional (mcp with auth_type 'none') is testable with
  // nothing stored, so the button must not be disabled for it.
  const needsCredentials = providerSpec
    ? visibleFields(providerSpec.credentialFields, scope).some((f) => f.required)
    : true;

  const credentialsTouched = Object.values(credentials).some((v) => v !== '' && v !== undefined && v !== null)
    && (!revealedSnapshot || JSON.stringify(credentials) !== JSON.stringify(revealedSnapshot));

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      if (isNew) {
        const created = await fetchApi('connectionCreate', {
          // The provider row is the relational anchor — its category comes with it.
          providerId: spec.providers[row.provider].id,
          name: row.name, description: row.description,
          application_id: row.application_id || null,
          config, ...(credentialsTouched ? { credentials } : {}),
        });
        nav(`/app-connection/${created.id}`, { replace: true });
      } else {
        const updated = await fetchApi('connectionUpdate', {
          id, name: row.name, description: row.description, is_active: !!row.is_active,
          application_id: row.application_id || null,
          config, ...(credentialsTouched ? { credentials } : {}),
        });
        setRow((r) => ({ ...r, has_credentials: updated.has_credentials, status: updated.status }));
        if (credentialsTouched) { setCredentials({}); setRevealedSnapshot(null); }
      }
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const remove = () => removeConnection({ fetchApi, id, name: row.name, nav, setErr });

  if (loading) return <PageShell><Loader text="Loading connection…" /></PageShell>;
  if (!row || !spec) return <PageShell><Alert variant="error" title="Could not load">{err || 'Connection not found'}</Alert></PageShell>;

  return (
    <PageShell width="narrow" className="space-y-3">
      <HeaderSection row={row} isNew={isNew} saving={saving} setField={setField} save={save} remove={remove} />

      {err && <Alert variant="error" title="Error">{err}</Alert>}

      <MetaSection row={row} isNew={isNew} categories={categories} categoryProviders={categoryProviders} setField={setField} onProviderContextChange={resetProviderState} apps={apps} />
      <ConfigSection providerSpec={providerSpec} config={config} setConfigField={setConfigField} setConfigValues={setConfig} scope={scope} isNew={isNew} />
      <CredentialsSection
        providerSpec={providerSpec}
        credentials={credentials}
        setCredential={setCredential}
        hasCredentials={!!row.has_credentials}
        connectionId={id}
        isNew={isNew}
        onRevealed={onRevealed}
        scope={scope}
      />
      <OAuthConnectSection
        connectionId={id}
        isNew={isNew}
        isOAuthAuthType={scope.auth_type === 'oauth_authorization_code'}
        onConnected={async () => {
          try {
            const res = await fetchApi('crud', { model: 'AppConnection', action: 'show', where: { id } });
            const c = res && res.data ? res.data : res;
            setRow((r) => ({ ...r, has_credentials: c ? !!c.has_credentials : r.has_credentials, status: c ? c.status : r.status }));
          } catch { /* re-read failure is non-fatal; the connect already succeeded */ }
        }}
      />
      <UsedBySection providers={usedBy} />
      {!isNew && row.category_key === 'ai-model' && <ModelCatalogSection connectionId={id} />}
      {!isNew && (
        <TestSection
          connectionId={id}
          hasCredentials={!!row.has_credentials}
          needsCredentials={needsCredentials}
          status={row.status}
          lastTestError={row.last_test_error}
          onTested={(res) => setRow((r) => ({ ...r, status: res.ok ? 'ok' : 'error', last_test_error: res.ok ? null : res.error }))}
        />
      )}
    </PageShell>
  );
}
